'use client';
import { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';

import { useTranslations } from 'next-intl';
import Head from 'next/head';
import * as Yup from 'yup';

import {
  AuthInput,
  AuthLayout,
  AuthPanel,
  AuthSubmitButton
} from '../../../components/Auth';
import { Link } from '../../../i18n/navigation';
import api from '../../../services/api';

interface FormValidationError {
  email: string;
}

export default function RequestPasswordReset() {
  const t = useTranslations();

  const [warning, setWarning] = useState<FormValidationError | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const schema = Yup.object().shape({
        email: Yup.string()
          .email(
            t('pages.request_password_reset.input_errors.email_invalid_format')
          )
          .required(
            t('pages.request_password_reset.input_errors.email_required')
          )
      });

      const isValid = await schema.validate({ email }, { abortEarly: false });

      if (isValid) {
        setWarning(null);
        await handlePasswordReset();
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        interface ValidationError extends FormValidationError {
          [key: string]: string;
        }

        const validationErrors: ValidationError = {
          email: ''
        };

        error.inner.forEach(validationError => {
          validationErrors[validationError.path as string] =
            validationError.message;
        });

        setWarning(validationErrors);
      }
    }
  }

  async function handlePasswordReset() {
    try {
      setLoading(true);
      const response = await api.post(`/v1/password-reset/request`, {
        email
      });

      setLoading(false);
      if (response.status === 201) {
        setEmail('');
        setRequestSent(true);

        toast(
          t(
            'pages.request_password_reset.messages.successfully_request_password_reset.message'
          ),
          {
            position: 'bottom-center',
            type: 'success'
          }
        );
      }
    } catch (err: any) {
      setLoading(false);
      const status = err?.response?.status;

      if (status === 404) {
        toast(
          t('pages.request_password_reset.messages.email_not_found.message'),
          {
            position: 'bottom-center',
            type: 'error'
          }
        );
      }

      if (status === 500) {
        toast(
          t(
            'pages.request_password_reset.messages.internal_server_error.message'
          ),
          {
            position: 'bottom-center',
            type: 'error'
          }
        );
      }
    }
  }

  return (
    <>
      <Head>
        <title>{`${t('pages.request_password_reset.head_title')}`}</title>
        <meta
          property="og:title"
          content={`${t('pages.request_password_reset.head_title')}`}
        />
        <meta
          key="description"
          name="description"
          content={t('pages.request_password_reset.description')}
        />
        <meta
          key="og:description"
          name="og:description"
          content={t('pages.request_password_reset.description')}
        />

        <meta
          key="og:image"
          name="og:image"
          content={'https://trakio.app/assets/og-preview.jpg'}
        />
      </Head>

      <AuthLayout
        sideTitle={t('landing.title')}
        sideDescription={t('landing.subtitle')}
        highlights={[
          t('landing.get_the_app.title'),
          t('landing.find_where_to_watch.title'),
          t('landing.import_trakt_data.title')
        ]}
      >
        <AuthPanel
          title={t('pages.request_password_reset.title')}
          description={t('pages.request_password_reset.description')}
          footer={
            <Link
              href="/login"
              className="font-bold text-white hover:text-red-600"
            >
              {t('sign_up.sign_in_button')}
            </Link>
          }
        >
          {requestSent && (
            <div className="mb-5 rounded-md border border-green-600/30 bg-green-600/10 p-3 text-sm leading-6 text-white">
              {t(
                'pages.request_password_reset.messages.successfully_request_password_reset.message'
              )}
            </div>
          )}

          <form className="flex flex-col" onSubmit={handleSubmit}>
            <AuthInput
              id="email"
              name="email"
              type="email"
              label={t('pages.request_password_reset.email')}
              placeholder={t('pages.request_password_reset.email_placeholder')}
              required
              value={email}
              autoComplete="email"
              error={warning?.email}
              onChange={e => setEmail(e.target.value)}
            />

            <div className="mt-6">
              <AuthSubmitButton
                label={t('pages.request_password_reset.submit_button')}
                loading={loading}
              />
            </div>
          </form>
        </AuthPanel>
      </AuthLayout>
    </>
  );
}
